define(function(require) {
    const DataGridTemplate = require('./DataGrid.template.html');
    const BaseView = require('app/Base.view');
    const _ = require('underscore');
    const BulkActionBarView = require('app/BulkActionBar.view');
    const DataGridColumnsView = require('app/DataGridColumns.view');
    const DataGridFilterDisplay = require('app/DataGridFilterDisplay.view');
    const Backbone = require('backbone');
    const BasicModel = require('models/app/BasicModel');
    const GridFiltersView = require('app/GridFilters.view');
    const ButtonView = require('components/Button');
    const CC = require('CC');
    const $ = require('jquery');
    const FileUtil = require('utils/file');
    const XLSX = require('xlsx');
    const UserSettingUtil = require('utils/userSetting');

    // Core
    require('datatables.net');
    require('datatables.net-dt/css/dataTables.dataTables.css');

    require('datatables.net-plugins/pagination/full_numbers_no_ellipses.js');

    require('jszip');

    require('datatables.net-buttons');
    require('datatables.net-buttons/js/buttons.html5.js');
    require('datatables.net-colreorder-dt');
    require('datatables.net-responsive-dt');
    require('datatables.net-responsive-dt/css/responsive.dataTables.css');
    require('datatables.net-rowreorder-dt');
    require('datatables.net-rowreorder-dt/css/rowReorder.dataTables.min.css');
    require('datatables.net-select');
    require('datatables.net-select-dt/css/select.dataTables.css');

    require('components/DataGrid.css');
    require('components/TextInput.css');

    // Orthogonal data constants: https://datatables.net/manual/data/orthogonal-data
    // Uncomment when use cases are discovered
    const DISPLAY_KEY = 'display';
    //const SORT_KEY = 'sort';
    const EXPORT_KEY = 'export';
    //const FILTER_KEY = 'filter';
    //const DATA_TYPE_KEY = 'type';

    const DataGrid = BaseView.extend(
        {
            template: _.template(DataGridTemplate),

            PageSizeSets: {
                DEFAULT: [10, 20, 50, 100, 500],  
                REDUCED: [10, 20, 50, 100]         
            },

            defaults: function() {
                return {
                    collection: undefined,
                    columns: [],
                    form: [], // Filters, leave blank to hide
                    settingsForm: [],
                    bulkActionOptions: {
                        showBulkActionName: true,
                        showClearButton: true,
                        maximumSelection: Number.MAX_SAFE_INTEGER,
                        maximumVisible: Infinity,
                        bulkActions: undefined // A list of actions (id, name, ccEventName)
                    },
                    addOns: [],
                    filename: 'data-grid',
                    clientId: CC.utils.getCurrentClientId(),
                    defaultPageLength: 20, // fallback default when all else fails
                    showPageLengthSelector: true,
                    deferLoading: false, // Don't fetch on page load
                    hasSearch: true,
                    hasPaging: true,
                    pagingType: 'full_numbers',
                    hasExport: true,
                    emptyTableMessage: 'Search or Select an Option from above',
                    noResultsMessage: 'No Results Found',
                    rowCallback: (row, data, displayNum, displayIndex, dataIndex) => {},
                    selectedCollection: new Backbone.Collection(),
                    showSelectionCheckboxes: DataGrid.SelectionCheckboxes.Inherit,
                    exportSheetName: 'Sheet 1', // Name of individual sheet on Excel export
                    columnManagerStartOffset: 1, // Set to 0 if a tables's columns aren't moving properly
                    stateVersion: 1, // Increment to clear out previous cached versions of the table state
                    savePageOffset: true,
                    bulkPageSize: 2000,
                    title: undefined,
                    enableVerticalScroll: false,
                    wrapHeaders: false,
                    searchPlaceholder: 'Search Text',
                    onRowClick: () => {},
                    pageSizeSet: 'DEFAULT' // DEFAULT|REDUCED
                };
            },

            events: {
                'click .data-grid__selectAll': 'onSelectAllClick',
                'click .dataTables_filter_clear': 'clearSearch',
                'input .dataTables_filter input': 'onSearch',
                'click .dropdown-menu': 'keepMenuOpen',
                'click .dropdown-menu.primary-actions__filter .dropdown-toggle': 'closeFilter'
            },

            keepMenuOpen: function(e) {
                if (!e.target.className.includes('dropdown-toggle')) {
                    e.stopPropagation();
                }
            },

            closeFilter: function() {
                this.$('.dropdown.primary-actions').removeClass('open');
            },

            initialize: function(options) {
                $.extend(true, this, this.defaults(), options);
                this.rowId = 'cid'; //Uniquely identify each row

                if (!this.hasValue(this.model)) {
                    this.model = new Backbone.Model();
                }
                if (!this.hasValue(this.settingsModel)) {
                    this.settingsModel = new Backbone.Model();
                }

                this.listenTo(this.collection, 'DataGrid:ReorderColumns', newOrder => {
                    this.table.colReorder.enable(true);
                    this.table.colReorder.order(newOrder);
                });

                this.listenTo(this.collection, 'DataGrid:Column::VisibilityChange', (name, isChecked) => {
                    const column = this.table.column(`${name}:name`);

                    column.visible(isChecked);
                    if (isChecked) {
                        if (this.tooltipColumns.length > 0) {
                            this.updateHeaderTooltips(this.tooltipColumns);
                        }
                    }

                    this.table.draw(false);
                });

                this.listenTo(this.collection, 'remove', (model, collection, options) => {
                    if (collection.mode != 'server') {
                        this.table
                            .rows(`#${model.id}`)
                            .remove()
                            .draw();
                    }
                });

                this.listenTo(this.collection, 'selectRow', model => {
                    this.selectRow(model.cid);
                    this.selectedCollection.add(model);
                });

                this.viewCellRegistry = [];
                this.listenTo(this.collection, 'DataGrid:ViewCell::Register', view => this.viewCellRegistry.push(view));

                this.listenTo(this.collection, 'DataGrid:ResetSort', this.resetToDefaultSort);

                this.stateKey = `${window.location.pathname}-${this.clientId}--${this.stateVersion}`;

                if (this.filters) {
                    this.form = this.filters.map(filter => {
                        if (_.isFunction(filter.filterType)) {
                            return filter.filterType.apply(this, [filter]);
                        } else {
                            return filter;
                        }
                    });
                }

                this.resizeObserver = new ResizeObserver(entries => {
                    if (this.table) {
                        this.table.columns.adjust();
                    }
                });
            },

            onClose: function() {
                this.resizeObserver?.disconnect();
            },

            render: function() {
                const self = this;
                if (this.table) {
                    // Clean up
                    this.viewCellRegistry.forEach(view => {
                        view.view.close();
                    });
                    this.table.clear();
                    this.table.destroy(true);
                    this.$('.data-grid__table').empty();
                }

                this.tooltipColumns = [];
                const hasBulkActions = this.bulkActionOptions && this.bulkActionOptions.bulkActions;
                const hasCheckBoxes =
                    this.showSelectionCheckboxes !== DataGrid.SelectionCheckboxes.Inherit ? true : hasBulkActions;
                const hasActions =
                    this.hasValue(this.actionOptions) &&
                    this.actionOptions.actions?.some(action => action.isVisible ?? true);
                const hasFilters = this.hasValue(this.form) && this.form.length > 0;
                const hasSettings = this.hasValue(this.settingsForm) && this.settingsForm.length > 0;
                const hasHeader =
                    hasActions || hasFilters || this.hasSearch || this.hasExport || hasBulkActions || this.hasSettings;
                const hasServerMode = this.collection.mode === 'server';
                const lengthControl = this.showPageLengthSelector ? 'l' : '<>';
                const rowReorderOptions = this.rowReorder || false;

                // Defer Loading if the collection already has the data
                let deferLoading =
                    this.deferLoading || (this.collection.length > 0 ? this.collection.state.totalRecords : null);
                if (!hasServerMode) {
                    deferLoading = true;
                }

                if (deferLoading) {
                    // The table will redraw once before we make an ajax call
                    this.collection.incrementDrawCounter();
                }

                const filteredColumns = this.columns.filter(Boolean).filter(column => {
                    if (column.options && _.isFunction(column.options.isIncluded)) {
                        return column.options.isIncluded();
                    } else {
                        return true;
                    }
                });

                this.preppedColumns = filteredColumns.map(column => {
                    if (column.CellClass) {
                        const cell = new column.CellClass(column.options, this);
                        return cell.toColumn();
                    } else {
                        const dataName = column.data || column.title || column.prefix || '';

                        const name = dataName.replace(/\./g, '-').toLowerCase();
                        column.className = `data-grid-col__${name}${column.className ? ` ${column.className}` : ''}`;
                        if (column.data !== null) {
                            column.name = name;
                        }
                        if (!_.isUndefined(column.tooltip)) {
                            this.tooltipColumns.push(column);
                        }
                        return column;
                    }
                });

                const displayColumns = this.preppedColumns.filter(column => !column.ExportOnlyCell);

                const viewOptions = {
                    hasActions: hasActions || hasBulkActions,
                    hasFilters: hasFilters,
                    hasSearch: this.hasSearch,
                    hasHeader: hasHeader,
                    hasPaging: this.hasPaging,
                    hasExport: this.hasExport,
                    hasFooter: this.hasFooter,
                    footerColumnCount: displayColumns.length,
                    barType: UserSettingUtil.getUserSetting('useUpdatedUIStyle') ? 'top' : 'toast',
                    title: this.title,
                    wrapHeaders: this.wrapHeaders
                };
                this.$el.html(this.template(viewOptions));

                // Update sorting for default column to not have the third intermediate step
                // Otherwise, you won't be able to cycle the sort on that column
                if (!_.isEmpty(this.collection.defaultSort)) {
                    const colIndex = displayColumns.findIndex(
                        column => column.name?.toLowerCase() == this.collection.defaultSort.sortKey?.toLowerCase()
                    );
                    if (colIndex > 0) {
                        displayColumns[colIndex].orderSequence = ['asc', 'desc'];
                    }
                }

                if (hasCheckBoxes) {
                    // Add a column for the select checkboxes
                    displayColumns.unshift({
                        data: null,
                        defaultContent: '',
                        orderable: false,
                        className: 'select-checkbox',
                        width: '20px'
                    });
                }

                $.fn.dataTable.ext.errMode = (settings, techNote, message) => {
                    this.renderAlert({
                        el: '.data-grid__message',
                        alertType: 'danger',
                        message: message.split(' - ')[1]
                    });

                    throw new Error(message);
                };
                let needsToSetUpListeners = true;
                let hasSkippedFirstFetch = false;
                this.table = this.$('.data-grid__table')
                    .on('draw.dt', (event, settings) => {
                        // Catches non-paged client sided grids
                        const api = settings.oInstance.api();
                        this.renderViewCells(api);
                    })
                    .DataTable(
                        _.extend(
                            {},
                            this.enableVerticalScroll && {
                                scrollY: '100%'
                            },
                            {
                                fixedColumns: true,
                                rowId: this.rowId,
                                autoWidth: false,
                                iShowPages: this.$('.data-grid').width() > 600 ? 5 : 2,
                                pagingType: this.pagingType,
                                processing: true,
                                serverSide: hasServerMode,
                                deferRender: true,
                                preDrawCallback: settings => {
                                    this.renderLoader();
                                },
                                drawCallback: settings => {
                                    if (this.$el) {
                                        this.$('.data-grid__table tbody').removeClass('hidden');
                                        this.$('.data-grid__footer').removeClass('hidden');
                                    }
                                },
                                footerCallback: function(row, data, start, end, display) {
                                    if (self.hasFooter) {
                                        const api = this.api();

                                        // Loop through each of the columns and push the footerData value
                                        //   through the display logic and put it in the proper footer column
                                        //   Note: this may not work perfectly with hidden columns, etc yet
                                        for (let index = 0; index < self.columns.length; index++) {
                                            const nextColumn = self.columns[index];
                                            if (api.column(index) && api.column(index).footer()) {
                                                api.column(index).footer().innerHTML = nextColumn.options.onDisplay(
                                                    null,
                                                    null,
                                                    null,
                                                    null,
                                                    nextColumn.options.footerData
                                                );
                                            }
                                        }
                                    }
                                },
                                rowCallback: this.rowCallback,
                                stateSave: true,
                                stateDuration: 0, // Indefinitely to local storage
                                stateSaveParams: (settings, data) => {
                                    // Adjust what gets saved and reloaded on refresh
                                    if (!this.savePageOffset) {
                                        data.start = undefined;
                                    }
                                },
                                stateSaveCallback: (settings, data) => {
                                    // Don't save these settings
                                    data.search = undefined;
                                    data.select = undefined;

                                    if (!this.hasPaging && this.defaultPageLength) {
                                        // If we are not paging, use the custom page length
                                        data.length = this.defaultPageLength;
                                    } else {
                                        data.length = this.getPageLength(); // look it up instead of relying on localStorage
                                    }

                                    if (data?.order?.length > 0) {
                                        if (data.order[0][1] === '') {
                                            // Sort was cleared, so there is no user set sorting info, therefore, get
                                            // the sort info from the collection itself
                                            const displayColumns = this.preppedColumns.filter(
                                                column => !column.ExportOnlyCell
                                            );
                                            data.order = this.collection.getOrder(displayColumns);
                                        }
                                    }

                                    localStorage.setItem(
                                        `DataTables-${this.viewEl}${this.stateKey}`,
                                        JSON.stringify(data)
                                    );
                                },
                                stateLoadCallback: settings => {
                                    return JSON.parse(
                                        localStorage.getItem(`DataTables-${this.viewEl}${this.stateKey}`)
                                    );
                                },

                                responsive: false,
                                colReorder: true,
                                rowReorder: rowReorderOptions,
                                orderClasses: false,
                                select: {
                                    style: 'multi',
                                    selector: '.select-checkbox'
                                },
                                search: {
                                    search: this.collection.getSearchParam()
                                },

                                order: this.collection.getOrder(displayColumns),
                                // If the collection already has data, don't request the first page from the api.
                                // Instead use the exiting models
                                deferLoading: deferLoading,

                                ajax: (data, callback, settings) => {
                                    if (this.backToDefaultSortRequested) {
                                        this.backToDefaultSortRequested = false;
                                        return;
                                    }

                                    let manualAbortInProgress = false;
                                    if (needsToSetUpListeners && this.$el) {
                                        this.listenTo(this.collection, 'request', (collection, xhr) => {
                                            const isRequestAGet =
                                                xhr &&
                                                xhr.originalRequestOptions &&
                                                xhr.originalRequestOptions.type === 'GET';
                                            if (this.xhr && isRequestAGet) {
                                                manualAbortInProgress = true;
                                                this.xhr.abort();
                                            }
                                            //  Save the xhr so we can abort previous calls
                                            this.xhr = xhr;
                                            this.table?.processing(true);
                                            this.clearAlert('.data-grid__message');
                                            this.$('.data-grid__table tbody').addClass('hidden');
                                            this.$('.data-grid__footer').addClass('hidden');
                                            this.$('.data-grid__message').empty();
                                            this.$('.data-grid__message').attr('data-processing', true);
                                        });
                                        this.listenTo(this.collection, 'sync', () => {
                                            delete this.xhr;

                                            this.table?.processing(false);
                                            this.$('.data-grid__message').attr('data-processing', false);
                                            callback(this.collection.toDataTables());

                                            this.$('[data-toggle="tooltip"]').tooltip({
                                                container: 'body'
                                            });
                                        });
                                        this.listenTo(this.collection, 'error', (collection, xhr) => {
                                            if (manualAbortInProgress) {
                                                manualAbortInProgress = false;
                                                return;
                                            }
                                            delete this.xhr;
                                            this.table?.processing(false);
                                            this.$('.data-grid__message').attr('data-processing', false);

                                            if (collection.models) {
                                                // This is an event from the collection itself
                                                callback({
                                                    error: CC.utils.getAjaxErrorMessage(
                                                        xhr,
                                                        'Failed to retrieve the data'
                                                    )
                                                });
                                            } else {
                                                // This is an event that has bubbled up
                                                this.reload();
                                            }
                                        });
                                        needsToSetUpListeners = false;
                                    }

                                    if (deferLoading && !hasSkippedFirstFetch) {
                                        callback(this.collection.toDataTables());
                                        hasSkippedFirstFetch = true;
                                        return;
                                    }
                                    const order = data.order[0];
                                    const pageSize = settings._iDisplayLength;
                                    this.collection.setSearchParam(data.search.value);

                                    if (order) {
                                        this.collection.setSorting(
                                            data.columns[order.column].data,
                                            order.dir === 'asc' ? -1 : 1
                                        );
                                    }

                                    this.collection.setPageSize(pageSize, {
                                        deferFetch: true
                                    });
                                    this.collection.getPage(data.start / pageSize + 1);
                                },
                                initComplete: (settings, json) => {
                                    this.$('[data-toggle="tooltip"]').tooltip({
                                        container: 'body'
                                    });

                                    this.$('.dt-search .text-input__input').focus();
                                    // Observe the table's container
                                    if (this.enableVerticalScroll) {
                                        this.resizeObserver.observe(this.$('.data-grid__table-wrapper')[0]);
                                    }
                                },
                                columns: displayColumns,
                                lengthChange: true,
                                lengthMenu: this.PageSizeSets[this.pageSizeSet] ?? this.PageSizeSets.DEFAULT,
                                pageLength: this.getPageLength(),
                                dom: `<"data-grid__header"  <"data-grid__filter-display"> f<B> > <"data-grid__table-wrapper"t> <"data-grid__footer" ${lengthControl} i p >`,
                                language: {
                                    info: '_START_ - _END_ of _MAX_',
                                    lengthMenu: '_MENU_',
                                    infoFiltered: '',
                                    processing:
                                        '<div class="data-grid__loader"><img src="/web/images/loading_148x148.gif" height="48" width="48"></div>',
                                    select: {
                                        rows: ''
                                    },
                                    search: '',
                                    searchPlaceholder: this.searchPlaceholder,
                                    emptyTable: this.emptyTableMessage,
                                    zeroRecords: this.noResultsMessage
                                },
                                buttons: [
                                    {
                                        tag: 'i',
                                        html: /*template*/ `
                                    <div class="dropdown drop-right inline-block primary-actions data-grid__actions--filters">
                                        <a
                                            id="filter"
                                            class="dropdown-toggle data-grid__filters"
                                            data-toggle="dropdown"
                                            role="button"
                                            aria-expanded="false"
                                        >
                                        <i class="lotacon lotacon-filter-line" title="Filters"/>
                                        </a>
                                        <div
                                            class="dropdown-menu primary-actions__filter"
                                            role="menu"
                                            id="filterDropdown"
                                            aria-labelledby="primary-action-filter"
                                        >
                                            <div class="filters__header">Filter <i class="lotacon lotacon-remove-circle dropdown-toggle pull-right"/></div>
                                            <div class="filters__content"></div>
                                        </div>
                                    </div>`
                                    },
                                    {
                                        tag: 'div',
                                        attr: {
                                            class: 'data-grid__actions--download'
                                        }
                                    },
                                    {
                                        tag: 'i',
                                        html: /*template*/ `
                                    <div class="dropdown drop-right inline-block primary-actions data-grid__actions--columns">
                                        <a
                                            id="columns"
                                            class="dropdown-toggle data-grid__columns"
                                            data-toggle="dropdown"
                                            role="button"
                                            aria-expanded="false"
                                        >
                                        <i class="lotacon lotacon-cog" title="Customize Display"/>
                                        </a>
                                        <div
                                            class="dropdown-menu primary-action__columns"
                                            role="menu"
                                            id="columnDropdown"
                                        >
                                            <div class="columns__header">Customize Display <i class="lotacon lotacon-remove-circle dropdown-toggle pull-right"/></div>
                                            <div class="columns__content"></div>
                                        </div>
                                    </div>`
                                    }
                                ],
                                stateLoaded: (settings, data) => {
                                    // Save the state and let everyone know it is ready
                                    this.state = data;
                                    this.collection.trigger('DataGrid:StateLoaded', data);
                                }
                            },
                            Object.assign({}, ...this.addOns)
                        )
                    );

                _.defer(() => {
                    if (!hasServerMode) {
                        // Re-adjust the columns sizes when client rendering
                        this.table.columns.adjust().draw();
                    }

                    if (this.$el) {
                        /* Inject a span so we can make sure the sort indicators are right next to the header labels */
                        this.preppedColumns.forEach(column => {
                            const $header = this.$(`th.data-grid-col__${column.name}`);
                            // First, clear out any existing wrapper
                            $header.find('.data-grid__table-header span').unwrap();

                            // Remove duplicate dt-column-title spans if they exist
                            const $titles = $header.find('.dt-column-title');
                            if ($titles.length > 1) {
                                $titles.slice(1).remove();
                            }

                            // Remove duplicate dt-column-order spans if they exist
                            const $orders = $header.find('.dt-column-order');
                            if ($orders.length > 1) {
                                $orders.slice(1).remove();
                            }

                            // Now wrap the cleaned-up spans
                            $header.find('span').wrapAll('<span class="data-grid__table-header"></span>');
                        });

                        this.table.on('order', (event, settings, order) => {
                            this.handleDefaultSortRequests(order, displayColumns);
                            return;
                        });
                    }
                });

                if (this.hasSearch) {
                    this.$('.data-grid .dt-search label').addClass('text-input');
                    this.$('.data-grid .dt-search input').addClass('text-input__input');
                }

                if (hasFilters) {
                    this.createAndRenderSubView('.filters__content', GridFiltersView, {
                        model: this.model,
                        settingsModel: this.settingsModel,
                        collection: this.collection,
                        form: this.form,
                        settingsForm: this.settingsForm
                    });

                    this.createAndRenderSubView('.data-grid__filter-display', DataGridFilterDisplay, {
                        model: this.model,
                        collection: this.collection,
                        form: this.form
                    });
                }

                this.createAndRenderSubView('.columns__content', DataGridColumnsView, {
                    collection: this.collection,
                    columns: displayColumns.map(column => {
                        return _.extend(column, {
                            isActive: this.table.column(`${column.name}:name`).visible()
                        });
                    }),
                    state: this.state,
                    startOffset: this.columnManagerStartOffset
                });

                if (hasActions) {
                    const firstAction = this.actionOptions.actions[0];
                    const primaryActionButton = this.createAndRenderSubView(
                        '.data-grid__button-bar--primary',
                        ButtonView,
                        {
                            buttonSize: this.actionOptions.buttonSize,
                            buttonStyle: firstAction.buttonStyle || 'button--secondary',
                            onChange: (e, model) => {
                                const changeFn = firstAction.onChange;
                                if (_.isFunction(changeFn)) {
                                    changeFn.apply(this, [e, model]);
                                }
                            },
                            actions: [firstAction],
                            isEnabled: () => {
                                return firstAction?.isEnabled !== false;
                            }
                        }
                    );
                    this.listenTo(this.collection, 'DataGrid:RefreshActions', () => {
                        primaryActionButton.render();
                    });

                    const secondAction = this.actionOptions.actions[1];
                    if (secondAction) {
                        const secondaryActionButton = this.createAndRenderSubView(
                            '.data-grid__button-bar--secondary',
                            ButtonView,
                            {
                                buttonStyle: secondAction.buttonStyle || 'button--secondary',
                                buttonSize: this.actionOptions.buttonSize,
                                onChange: (e, model) => {
                                    const changeFn = secondAction.onChange;
                                    if (_.isFunction(changeFn)) {
                                        changeFn.apply(this, [e, model]);
                                    }
                                },
                                actions: [secondAction]
                            }
                        );
                        this.listenTo(this.collection, 'DataGrid:RefreshActions', () => {
                            secondaryActionButton.render();
                        });
                    }
                }

                const spreadsheetFormat = FileUtil.getSpreadsheetFormat() == 'xlsx' ? 'Excel' : 'CSV';
                this.createAndRenderSubView('.data-grid__actions--download', ButtonView, {
                    buttonStyle: 'button--link',
                    actions: [
                        {
                            name: 'downloadSpreadsheet',
                            icon: 'lotacon lotacon-download-line'
                        }
                    ],
                    waitingText: this.smallLoader,
                    tooltip: `Download Table to ${spreadsheetFormat}`,
                    onChange: (e, model) => {
                        return this.handleExport(this.bulkPageSize);
                    }
                });

                this.$('i').tooltip();

                if (hasCheckBoxes) {
                    this.$('th.select-checkbox').html(`<div class="data-grid__selectAll"></div>`);

                    this.table.on('select', (e, dt, type, indexes) => {
                        const ids = this.table
                            .rows({
                                selected: true
                            })
                            .ids()
                            .toArray();
                        const collection = this.collection.fullCollection
                            ? this.collection.fullCollection
                            : this.collection;
                        const matchingModels = collection
                            .chain()
                            .filter(model => {
                                const isInTheIdList = ids.indexOf(String(model[this.rowId])) !== -1;
                                return isInTheIdList;
                            })
                            .reject(model => {
                                // Since the model's get cloned below which is needed to support deletes and such,
                                // we need to manually find the matches as opposed to the lookup in done by set below
                                const existingSelections = this.selectedCollection.find(selectedModel => {
                                    return (
                                        selectedModel.cid === model.cid ||
                                        (selectedModel.id && selectedModel.id == model.id)
                                    );
                                });
                                return existingSelections;
                            })
                            .map(model => {
                                // BasicGrid had the model embedded so maintain
                                const cloned = model.clone();
                                cloned.set('model', model.clone());
                                return cloned;
                            })
                            .value();

                        this.selectedCollection.set(matchingModels, {
                            remove: false
                        });
                        if (hasBulkActions) {
                            this.renderBulkActions(this.selectedCollection, dt);
                        }
                    });

                    this.table.on('deselect', (e, dt, type, indexes) => {
                        if (type === 'row') {
                            const data = this.table
                                .rows(indexes)
                                .data()
                                .pluck(this.rowId);
                            const id = data.toArray()[0];

                            const model = this.collection.get(id);
                            if (model) {
                                this.selectedCollection.remove(model.id);
                            } else {
                                console.warn("Couldn't find model => most likely the collection does not have an id");
                            }

                            this.renderBulkActions(this.selectedCollection, dt);
                        }
                    });
                }

                this.table.on('click', 'tbody tr', event => {
                    const $row = $(event.currentTarget);
                    // Do not process this if we clicked in the bulk select checkbox
                    if ($(event.target).hasClass('select-checkbox')) {
                        return;
                    }

                    // Do not process this if we clicked in the action icons
                    if ($(event.target).hasClass('action-item')) {
                        return;
                    }

                    // Process what the view may want
                    this.onRowClick($row, this.table);

                    // Process any detail rows
                    const expanderColumn = displayColumns.find(column => column.prefix === 'expander');
                    if (expanderColumn) {
                        const row = this.table.row($row);
                        if (row.child.isShown()) {
                            row.child.hide();
                            $row.removeClass('shown').removeClass('details');
                        } else {
                            // Hide all the other rows
                            this.table.rows().every(function() {
                                if (this.child.isShown()) {
                                    this.child.hide();
                                    $(this.node())
                                        .removeClass('shown')
                                        .removeClass('details');
                                }
                            });

                            row.child(expanderColumn.expanderContent(row.data())).show();
                            $row.addClass('shown').addClass('details');
                        }
                    }
                });

                this.renderSearchControl();

                if (this.tooltipColumns.length > 0) {
                    this.updateHeaderTooltips(this.tooltipColumns);
                }

                return this;
            },

            onSearch: function(e) {
                this.resetToSort();
            },

            resetToDefaultSort: function() {
                this.collection.resetSorting();
                // Reset the sort display
                this.table.order([]).draw();
            },

            handleDefaultSortRequests: function(order, displayColumns) {
                // When the third click sort event is requested,
                // switch to the default sort for this collection
                if (order.length === 0) {
                    if (!this.backToDefaultSortRequested) {
                        this.backToDefaultSortRequested = true;
                        this.collection.resetSorting();
                        this.table.order(this.collection.getOrder(displayColumns)).draw();
                    }
                }
            },

            getTable: function() {
                return this.table;
            },

            getColumnsToExport: function() {
                return this.preppedColumns
                    .filter(column => column.data)
                    .map(column => {
                        return Object.assign({}, column, {
                            col: column.data,
                            displayName: column.title
                        });
                    });
            },

            handleExport: function(bulkPageSize) {
                this.clearAlert('.data-grid__message');
                return this.collection
                    .getAllPages(bulkPageSize)
                    .fail(message => {
                        this.renderAlert({
                            el: '.data-grid__message',
                            alertType: 'danger',
                            message: message
                        });
                    })
                    .done(exportCollection => {
                        // Add the footer to the export collection exactly once
                        if (this.hasFooter && exportCollection.every(model => !model.isFooterModel)) {
                            const footerModel = new BasicModel();
                            this.columns.forEach(column => {
                                footerModel.set(column.options.data, column.options.footerData);
                            });

                            // We capture the first time the footer model is added and then skip afterwards if it already exists
                            // This can happen if changes that re-render the grid do not fetch new data
                            // If we did not do this, it would keep adding the footer model and have duplicates
                            // on subsequent exports
                            footerModel.isFooterModel = true;
                            exportCollection.add(footerModel);
                        }
                        const filename = _.result(this, 'filename', 'data-grid');
                        if (FileUtil.getSpreadsheetFormat() == 'xlsx') {
                            this.exportToExcel(exportCollection, filename);
                        } else {
                            this.exportToCSV(exportCollection, filename);
                        }
                    });
            },

            exportToExcel: function(exportCollection, filename) {
                const columnsToExport = this.getColumnsToExport();
                const columnDefinitions = [];

                // Calculate the percentage width per column based on how it currently looks in the UI
                const tableWidth = this.$('.data-grid__table').width();
                const defaultWidth = columnsToExport.length > 0 ? tableWidth / columnsToExport.length : tableWidth;
                let totalWidth = 0;
                const widths = [];
                const columnNameHash = {};
                columnsToExport.forEach(column => {
                    columnNameHash[column.col] = column.displayName ? column.displayName : column.name;
                    let width = this.$(`.data-grid__table thead .${column.className}`).width();
                    if (!width || width < 0) {
                        width = defaultWidth;
                    }
                    totalWidth += width;
                    widths.push(width);
                });
                totalWidth = totalWidth || 100;
                // The `t: 's'` below means that all cells are the string type.
                // There's not a good way to infer this type from the table. CSS isn't always available.
                // Not all columns use a DataGrid Cell, so we can't key off of that.
                widths.forEach(width => {
                    columnDefinitions.push({
                        wch: Math.round((width / totalWidth) * 100),
                        t: 's' // just doing all strings for now
                    });
                });

                // Convert collection to array of objects with <Column Name>: <formatted value> attributes.
                // This is similar to FileUtil.formatCollectionForExport()
                const data = exportCollection.map(model => {
                    const attrs = {};
                    columnsToExport.forEach(column => {
                        const displayName = column.displayName ? column.displayName : column.col;
                        let rawValue = model.get(column.col);
                        let cellValue = '';
                        // Look for '.' separator in column name, and split on that if found.
                        // This is currently only used in a Backgrid table, see
                        // pages/audience/FacebookExportBeacon.view.js
                        if (column.col.indexOf('.') !== -1) {
                            const names = column.col.split('.');
                            const field = model.get(names[0]);
                            rawValue = _.isFunction(field.get) ? field.get(names[1]) : field[names[1]];
                            // Support <field.field.field>
                            if (names.length == 3) {
                                rawValue = rawValue[names[2]];
                            }
                        }
                        if (!_.isUndefined(rawValue)) {
                            if (!_.isUndefined(column.exportFormatter)) {
                                // Use the explicitly set export formatter function
                                cellValue = column.exportFormatter(rawValue, model);
                            } else if (!_.isUndefined(column.formatter) && column.cell !== 'html') {
                                // Use the custom formatter for non-html cells
                                cellValue = column.formatter.fromRaw(rawValue, model) || '';
                            } else if (!_.isUndefined(column.render)) {
                                // Use the Datagrid render method
                                // The grid may not have the model if not on the current page, so pass it
                                cellValue = column.render(rawValue, 'export', null, null, model);
                            } else {
                                cellValue = rawValue;
                            }
                        }
                        attrs[displayName] = cellValue;
                    });
                    return attrs;
                });
                const wb = XLSX.utils.book_new();
                const ws = XLSX.utils.json_to_sheet(data);
                ws['!cols'] = columnDefinitions;

                XLSX.utils.book_append_sheet(wb, ws, this.exportSheetName);
                XLSX.writeFile(wb, `${filename}.xlsx`, {
                    cellStyles: true
                });
            },

            exportToCSV: function(exportCollection, filename) {
                const columnsToExport = this.getColumnsToExport();
                const data = FileUtil.formatCollectionForExport(exportCollection, columnsToExport, true);
                FileUtil.buildCSVFile(data, filename);
            },

            renderLoader: function() {
                if (this.$el) {
                    this.$('.data-grid__table tbody').addClass('hidden');
                    this.$('.data-grid__footer').addClass('hidden');
                }
            },

            renderViewCells: function(api) {
                //
                // Attach the views to the dom.
                // Specifically needed for ViewCells and Client side rendering
                //
                this.viewCellRegistry.forEach(view => {
                    // Grab the element where the view will be assigned
                    const viewEl = `.${view.viewPrefix}-${view.rowId}`;

                    // Close the current view on that element
                    const currentView = this.getSubView(viewEl);
                    if (!_.isUndefined(currentView)) {
                        currentView.close({
                            // eslint-disable-next-line camelcase
                            keep_element: true
                        });
                    }

                    // Assign the new view
                    this.assign(viewEl, view.view);
                });
                api.rows().every((rowIdx, tableLoop, rowLoop) => {
                    const row = api.row(rowIdx);
                    const data = row.data();
                    if (this.selectedCollection.get(data.id)) {
                        this.selectRow(data[this.rowId]);
                    }
                });
            },

            getData: function() {
                return this.collection.length > 0
                    ? this.collection.toJSON({
                          includeCid: true
                      })
                    : null;
            },

            onSelectAllClick: function(e) {
                if (this.selectedCollection.length > 0) {
                    this.table.rows().deselect();
                    this.selectedCollection.reset();
                    this.renderBulkActions(this.selectedCollection, this.table);
                    return;
                }

                this.table.rows({ page: 'all' }).select();
            },

            renderBulkActions: function(selectedCollection, dt) {
                if (selectedCollection.length === 0) {
                    this.$('.data-grid__selectAll').attr('data-state', 'none');
                    this.$('.data-grid__bulk').addClass('hidden');
                    return;
                }
                this.$('.data-grid__bulk').removeClass('hidden');

                this.bulkActionBar = this.createAndRenderSubView('.data-grid__bulk', BulkActionBarView, {
                    selectedList: selectedCollection,
                    bulkActions: this.bulkActionOptions.bulkActions,
                    showBulkActionName: this.bulkActionOptions.showBulkActionName,
                    maximumVisible: this.bulkActionOptions.maximumVisible,
                    selectionPostfix:
                        this.bulkActionOptions.maximumSelection === Number.MAX_SAFE_INTEGER
                            ? ''
                            : `/${this.bulkActionOptions.maximumSelection}`,
                    showClearButton: this.bulkActionOptions.showClearButton
                });
                this.listenTo(this.bulkActionBar, 'deselectAll', () => {
                    this.selectedCollection.reset();
                    this.table.rows().deselect(
                        this.table.rows({
                            selected: true
                        })
                    );
                });
                this.listenTo(this.bulkActionBar, 'removeSelection', this.selectionRemoved);

                if (
                    dt.rows().count() ===
                    dt
                        .rows({
                            selected: true
                        })
                        .count()
                ) {
                    // Select all items button.
                    this.$('.data-grid__selectAll').attr('data-state', 'all');
                    return;
                }

                // Deselect some items button.
                this.$('.data-grid__selectAll').attr('data-state', 'some');
            },

            getSelectedModels: function() {
                const selectedModels = this.selectedCollection.map(model => {
                    return model.get('model');
                });
                return selectedModels;
            },

            clearSelected: function() {
                this.selectedCollection.reset();
                this.table.rows().deselect(
                    this.table.rows({
                        selected: true
                    })
                );
            },

            selectRow: function(id) {
                this.table.rows(`#${id}`).select();
            },

            deSelectRow: function(id) {
                this.selectedCollection.remove(id);
                this.table.rows(`#${id}`).deselect();
            },

            reload: function() {
                this.clearSelected();

                this.table.ajax.reload();
            },

            updateHeaderTooltips: function(columns) {
                columns.forEach(column => {
                    if (!_.isUndefined(column.className)) {
                        const header = this.$(`th.${column.className.replace(' ', '.')}`);
                        if (header) {
                            const tooltipPlacement = _.isUndefined(column.tooltipPlacement)
                                ? 'bottom'
                                : column.tooltipPlacement;
                            header.attr('title', column.tooltip);
                            header.attr('data-toggle', 'tooltip');
                            header.attr('data-placement', tooltipPlacement);
                            if (column.tooltipAsHtml === true) {
                                header.attr('data-html', true);
                            }
                            header.tooltip({
                                container: 'body'
                            });
                        }
                    }
                });
            },

            renderSearchControl: function() {
                this.$('.dataTables_filter label.text-input').append(
                    /*template*/
                    `<i class="lotacon lotacon-close-line dataTables_filter_clear hidden"></i>`
                );
                this.table.on('search.dt', () => this.updateSearchControl());
                this.updateSearchControl();
            },

            updateSearchControl: function() {
                const searchStr = this.table.search() ? this.table.search().trim() : '';
                if (searchStr == '') {
                    this.$('.dataTables_filter_clear').addClass('hidden');
                } else {
                    this.$('.dataTables_filter_clear').removeClass('hidden');
                }
            },

            clearSearch: function() {
                this.table.search('').draw();
            },

            getPageLength: function() {
                let pageLength = this.defaultPageLength;
                // Try for the collection as a source of record, as it already has logic to do this.
                if (this.collection) {
                    pageLength = this.collection.getPageSize();
                } else {
                    // look for user settings
                    const userSetLength = UserSettingUtil.getUserSetting('defaultListLength');
                    if (typeof userSetLength !== 'undefined') {
                        pageLength = parseInt(userSetLength, 10);
                    }
                }
                return pageLength;
            }
        },
        {
            Cells: require('./DataGrid/ColumnBuilder').Cells,
            ActionBuilder: require('./DataGrid/ActionBuilder').ActionBuilder,
            ColumnBuilder: require('./DataGrid/ColumnBuilder').ColumnBuilder,
            // This is just a utility passthrough as many grids don't have FileUtil loaded independently
            generateFilename: function(options) {
                return FileUtil.generateFilename(options);
            },
            RadioFilter: GridFiltersView.RadioFilter,
            CheckboxFilter: GridFiltersView.CheckboxFilter,
            SelectFilter: GridFiltersView.SelectFilter,
            SelectionCheckboxes: {
                Inherit: 'inherit', // Use the built in logic regarding actions determine when to show the checkboxes
                On: 'on' // Override the usual logic as the checkboxes are needed for a custom usage
            }
        }
    );

    return DataGrid;
});
